import { localStorageValue } from "./LocalStorage"
import { Native } from "./Native"
import {
  createKeepAliveController,
  shouldKeepAlive,
  type KeepAliveController as RuntimeKeepAliveController,
} from "app-base/runtime/keep-alive"

// Per-device opt-in for background network keep-alive. Stored locally (never
// synced) like `logbook-companion`, so each crew phone decides for itself whether
// it should stay resident on the LAN to receive the alert fan-out while locked.
const KEEP_ALIVE_KEY = "logbook-keepalive";

export interface KeepAliveInputs {
  // An anchor watch is running on this device's ship, so alerts can fire. Keep-alive
  // is scoped to this so it is never permanently on.
  watchActive: boolean;
  // This device opted in to background keep-alive.
  optedIn: boolean;
  // The primary already self-keeps via NMEA GPS, so it never needs the
  // companion keep-alive.
  isPrimary: boolean;
}

// Pure decision: whether this device should hold the background network keep-alive.
export function keepAliveActive(inputs: KeepAliveInputs): boolean {
  return shouldKeepAlive({
    enabled: inputs.optedIn,
    active: inputs.watchActive,
    blocked: inputs.isPrimary,
  });
}

// Whether this device has opted in to background keep-alive.
export function keepAliveOptedIn(): boolean {
  return localStorageValue(KEEP_ALIVE_KEY) == "1";
}

// Persist this device's opt-in choice.
export function setKeepAliveOptIn(value: boolean): void {
  if (value) {
    localStorage.setItem(KEEP_ALIVE_KEY, "1");
  } else {
    localStorage.removeItem(KEEP_ALIVE_KEY);
  }
}

// The slice of the native bridge the controller drives; injectable for testing.
export interface KeepAliveSink {
  setNetworkKeepAlive(active: boolean): void;
}

// Reconciles the desired keep-alive state against the native bridge, calling it only
// when the decision actually changes so a re-render / repeated advice update does not
// spam the shell. One long-lived instance is held on the anchor-watch advice.
export class KeepAliveController {
  private controller: RuntimeKeepAliveController;

  constructor(sink: KeepAliveSink = Native) {
    this.controller = createKeepAliveController({
      sink: (active) => sink.setNetworkKeepAlive(active),
    });
  }

  reconcile(inputs: KeepAliveInputs): void {
    this.controller.update(keepAliveActive(inputs));
  }
}
